import React, { useState, useEffect, useMemo, useCallback } from 'react';
import loadingStyles from '../../styles/LoadingLogo.module.css';
import { Card } from '../../components/ui/Card';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Maximize2,
  Info,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ZAxis,
  Cell,
} from 'recharts';

const API = 'https://api.glowytics.com/api/google-ads';

const BCG_COLORS = {
  'Stars': '#10b981',
  'Question Marks': '#f59e0b',
  'Cash Cows': '#3b82f6',
  'Dogs': '#9ca3af',
  'No Sales': '#ef4444',
  'Insufficient Data': '#94a3b8',
};

// The five rows of the matrix table, in reading order. "No Sales" and
// "Insufficient Data" are real tiers, not footnotes: for most catalogues they
// hold the majority of the SKUs, and hiding them is what made earlier versions
// of this page look tidier than the data actually was.
// `blurb` is the one line that shows; `detail` is the full reasoning, surfaced
// on hover and in the methodology panel. The table used to print the whole
// paragraph in every row, which is most of why it read as a wall of text.
const ROWS = [
  {
    key: 'Stars', icon: '★', tone: 'green',
    blurb: 'Sells often and earns well per view.',
    detail: 'Above-median conversion AND above-median revenue per view. Protect and scale.',
  },
  {
    key: 'Question Marks', icon: '?', tone: 'yellow',
    blurb: 'Expensive, looked at, rarely bought.',
    detail: 'High revenue per view, low conversion. Because RPV = CR x AOV, this quadrant is mostly the high-price end of the catalogue — treat it as a price and merchandising question before a CRO one.',
  },
  {
    key: 'Cash Cows', icon: '💰', tone: 'blue',
    blurb: 'Converts well, earns little per view.',
    detail: 'Below-median revenue per view but above-median conversion — typically low-ticket. Also holds below-median products that sit inside the revenue core (Pareto promotion), so part of this row converts worse than the label suggests.',
  },
  {
    key: 'Dogs', icon: '🐕', tone: 'gray',
    blurb: 'Below median on both — plus everything that sold nothing.',
    detail: 'Below median on both axes. Zero-sellers are assigned here by rule rather than by the formula: with no sales both axes collapse to the same function of view count, so their position would encode traffic and nothing else.',
  },
  {
    key: 'Insufficient Data', icon: '🚫', tone: 'slate',
    blurb: 'Below the minimum-views floor.',
    detail: 'Too little traffic to measure. Reported for completeness, never used as evidence — though note it still contains products that sold.',
  },
];

const TONE = {
  green: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', chip: 'bg-emerald-100', hover: 'hover:bg-emerald-50', bar: 'bg-emerald-500' },
  yellow: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', chip: 'bg-amber-100', hover: 'hover:bg-amber-50', bar: 'bg-amber-500' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', chip: 'bg-blue-100', hover: 'hover:bg-blue-50', bar: 'bg-blue-500' },
  gray: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', chip: 'bg-gray-100', hover: 'hover:bg-gray-50', bar: 'bg-gray-400' },
  red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', chip: 'bg-red-100', hover: 'hover:bg-red-50', bar: 'bg-red-500' },
  slate: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', chip: 'bg-slate-100', hover: 'hover:bg-slate-50', bar: 'bg-slate-400' },
};

// Bars are drawn against a fixed domain rather than each cell's own maximum, so
// a bar of the same length always means the same share — across rows, across
// months, and between the two metrics stacked in one cell.
const SHARE_SCALE_MAX = 50;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const getMonthName = (monthKey) => {
  if (!monthKey) return '—';
  const [year, month] = monthKey.split('-');
  return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`;
};

const tl = (v, digits = 0) => `₺${Number(v || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: digits, maximumFractionDigits: digits,
})}`;
const int = (v) => Number(v || 0).toLocaleString('tr-TR');
const pct = (v, digits = 2) => `${Number(v || 0).toFixed(digits)}%`;

const DEMO_BRAND_ID_SC = '2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a';

export default function ProductPortfolioAnalysis() {
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [grouping, setGrouping] = useState('item');
  const [matrixMode, setMatrixMode] = useState('performance'); // 'performance' | 'growth'
  const [logScale, setLogScale] = useState(true);
  const [scatterChartExpanded, setScatterChartExpanded] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);
  const [useRunRate, setUseRunRate] = useState(false);
  // Export only the products whose quadrant has actually held across the last
  // closed months. A single month's membership churns by 60-80%, so a set built
  // from one month is mostly re-learning noise.
  const [stableOnly, setStableOnly] = useState(true);
  // The table's default is the share comparison — where attention goes against
  // where revenue comes from. The per-metric detail is one click away rather
  // than printed in all 30 cells at once.
  const [density, setDensity] = useState('compact');

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;

  const handleExportCategoryIds = async (category) => {
    try {
      if (!selectedMonthKey || !brandId) return;
      const url = `${API}/bcg-analysis/category-products`
        + `?month=${encodeURIComponent(selectedMonthKey)}`
        + `&category=${encodeURIComponent(category)}`
        + `&grouping=${encodeURIComponent(grouping)}`
        + (stableOnly ? '&stable=1' : '');
      const resp = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (!resp.ok) throw new Error(`Export request failed: ${resp.status}`);
      const data = await resp.json();
      const ids = (data.products || [])
        .map(p => p.itemId || p.productId || p.productName)
        .filter(Boolean);
      const csv = ids.join(',');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${category.replace(/\s+/g, '-')}-${selectedMonthKey || 'month'}${stableOnly ? '-stable' : ''}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error('Export failed', e);
    }
  };

  const fetchBcgData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!brandId) {
        setError('No brand ID found');
        setLoading(false);
        return;
      }
      const response = await fetch(`${API}/bcg-analysis?months=6&grouping=${grouping}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      const data = await response.json();

      if (brandId === DEMO_BRAND_ID_SC && data.monthlyAnalysis) {
        const suffixes = [' Ultra', ' Flex', ' Pro', ' Max', ' Elite', ' Trail', ' Air'];
        data.monthlyAnalysis.forEach(month => {
          const manipulate = (products) => {
            if (!products) return;
            products.forEach((p, idx) => {
              const base = String(p.productName || '');
              const ensureBrand = /^skechers/i.test(base) ? base : `Skechers ${base}`;
              const suffix = suffixes[(idx + (ensureBrand.length % suffixes.length)) % suffixes.length];
              p.productName = `${ensureBrand}${suffix}`;
            });
          };
          manipulate(month.categorizedProductsAll);
          manipulate(month.excludedProductList);
          manipulate(month.excludedCategorizedAll);
          Object.values(month.productLists || {}).forEach(manipulate);
        });
      }
      setProductData(data);
    } catch (err) {
      console.error('Error fetching BCG data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, grouping]);

  useEffect(() => { fetchBcgData(); }, [fetchBcgData]);

  // Memoised so the effect below does not see a fresh array identity on every
  // render and re-run for nothing.
  const months = useMemo(() => productData?.monthlyAnalysis || [], [productData]);
  const availableMonths = useMemo(() => productData?.availableMonths || [], [productData]);

  useEffect(() => {
    if (!availableMonths.length) return;
    if (!selectedMonthKey || !availableMonths.includes(selectedMonthKey)) {
      setSelectedMonthKey(productData?.referenceMonthKey && availableMonths.includes(productData.referenceMonthKey)
        ? productData.referenceMonthKey
        : availableMonths[0]);
    }
  }, [availableMonths, selectedMonthKey, productData]);

  const selectedMonth = useMemo(
    () => months.find(m => m.monthKey === selectedMonthKey) || null,
    [months, selectedMonthKey]
  );

  const toggleCategory = (category) =>
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));

  // ── Scatter ────────────────────────────────────────────────────────────────
  // Only the converting tier can be plotted. Products with no sales have
  // cr = rpv = 0 and would pile up on the origin, which is what made the old
  // chart a single blob.
  const scatterData = useMemo(() => {
    if (!selectedMonth) return { products: [], xThreshold: 0, yThreshold: 0, dropped: 0 };
    const th = selectedMonth.thresholds || {};

    if (matrixMode === 'growth') {
      const src = (selectedMonth.categorizedProductsAll || []).filter(p => p.revenueGrowth !== null && !p.isNew);
      const products = src.map(p => ({
        name: p.productName || p.itemId || 'Unknown',
        itemId: p.itemId,
        x: Math.max(p.relativeShare || 0, 1e-6),
        y: p.revenueGrowth,
        revenue: Math.round(p.totalRevenue || 0),
        views: p.totalViews, purchases: p.totalPurchases,
        cr: p.cr, rpv: p.rpv, aov: p.aov,
        category: p.growthCategory || 'Dogs',
        imageLink: p.imageLink || null,
        badge: p.paretoCore ? 'Revenue core' : null,
      }));
      return {
        products,
        xThreshold: Math.max(selectedMonth.growth?.shareThreshold || 0, 1e-6),
        yThreshold: selectedMonth.growth?.growthThreshold || 0,
        dropped: (selectedMonth.categorizedProductsAll || []).length - src.length,
      };
    }

    const src = (selectedMonth.categorizedProductsAll || [])
      .filter(p => !logScale || (p.rpvShrunk > 0 && p.crShrunk > 0));
    const products = src.map(p => ({
      name: p.productName || p.itemId || 'Unknown',
      itemId: p.itemId,
      x: p.rpvShrunk,
      y: p.crShrunk,
      revenue: Math.round(p.totalRevenue || 0),
      views: p.totalViews, purchases: p.totalPurchases,
      cr: p.cr, rpv: p.rpv, aov: p.aov,
      category: p.category,
      imageLink: p.imageLink || null,
      badge: p.workhorse ? 'Revenue core' : (p.isOutOfStock ? 'Out of stock' : null),
    }));
    return {
      products,
      xThreshold: th.rpvThreshold ?? th.rpvMedian ?? 0,
      yThreshold: th.crThreshold ?? th.crMedian ?? 0,
      dropped: (selectedMonth.categorizedProductsAll || []).length - src.length,
    };
  }, [selectedMonth, matrixMode, logScale]);

  // Recharts renders an empty plot when a log axis is handed an 'auto' domain,
  // so the bounds are computed from the data (and padded) rather than inferred.
  const axisDomains = useMemo(() => {
    const pts = scatterData.products;
    if (!pts.length) return { x: ['auto', 'auto'], y: ['auto', 'auto'] };
    const xs = pts.map(p => p.x).filter(v => Number.isFinite(v));
    const ys = pts.map(p => p.y).filter(v => Number.isFinite(v));
    const bounds = (arr, log) => {
      if (!arr.length) return ['auto', 'auto'];
      let lo = Math.min(...arr);
      let hi = Math.max(...arr);
      if (log) {
        lo = Math.max(lo, 1e-9) * 0.8;
        hi = hi * 1.25;
        if (!(hi > lo)) hi = lo * 10;
      } else {
        const pad = (hi - lo) * 0.08 || Math.abs(hi || 1) * 0.1;
        lo -= pad; hi += pad;
      }
      return [lo, hi];
    };
    const xLog = logScale;
    const yLog = logScale && matrixMode !== 'growth';
    return { x: bounds(xs, xLog), y: bounds(ys, yLog) };
  }, [scatterData, logScale, matrixMode]);

  const rowValue = (month, rowKey) => {
    if (rowKey === 'Insufficient Data') return month.excluded || {};
    return (month.categories || {})[rowKey] || {};
  };

  const rowProducts = (rowKey) => {
    if (!selectedMonth) return [];
    if (rowKey === 'Insufficient Data') return selectedMonth.excludedProductList || [];
    return (selectedMonth.productLists || {})[rowKey] || [];
  };

  const scaleFor = (month, v) =>
    (useRunRate && month?.coverage?.runRateFactor > 1 ? v * month.coverage.runRateFactor : v);

  // The like-for-like panel describes the month in progress, not whichever month
  // the table is pointed at — the page opens on the last closed month, so tying
  // it to the selection meant it never appeared.
  const liveMonth = useMemo(
    () => months.find(m => m.equalPeriod && m.equalPeriod.days > 0) || null,
    [months]
  );

  const stale = productData?.stale;
  const computedAt = productData?.computedAt ? new Date(productData.computedAt) : null;
  const methodologyVersion = productData?.methodologyVersion;
  const anyPartial = months.some(m => m.coverage?.isPartial);

  return (
    <div className="p-6 space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between bg-white p-6 rounded-2xl shadow-sm flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <img src="/Google-Analytics-Logo.png" alt="Analytics Icon" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Product Portfolio Analysis</h1>
              <p className="text-sm text-gray-500 mt-1">
                Strategic analysis of your product portfolio performance
                {methodologyVersion ? ` · methodology v${methodologyVersion}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(productData?.availableGroupings || []).length > 1 && (
              <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                {['item', 'group'].map(g => (
                  <button
                    key={g}
                    onClick={() => setGrouping(g)}
                    className={`px-3 py-1.5 text-xs font-medium transition ${
                      grouping === g ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    title={g === 'item' ? 'One row per SKU' : 'Variants rolled up to the Merchant Center item group'}
                  >
                    {g === 'item' ? 'Per SKU' : 'Per variant group'}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => setShowMethodology(v => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              <Info className="w-3.5 h-3.5" />
              How this is calculated
            </button>
          </div>
        </div>

        {/* Freshness / partial-month banners */}
        {!loading && computedAt && (
          <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm border ${
            stale ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-gray-50 border-gray-200 text-gray-600'
          }`}>
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>
              Data computed {computedAt.toLocaleString('tr-TR')}
              {stale && ' — this is more than 36 hours old, so the nightly job may not be completing.'}
            </span>
          </div>
        )}
        {!loading && anyPartial && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl text-sm border bg-blue-50 border-blue-200 text-blue-800 flex-wrap">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>
                One or more months are incomplete. Comparing a partial month against full ones understates it.
              </span>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={useRunRate} onChange={e => setUseRunRate(e.target.checked)} className="rounded" />
              <span className="text-xs font-medium">Scale partial months to a full-month run rate</span>
            </label>
          </div>
        )}

        {!loading && liveMonth && (
          <Card className="rounded-2xl">
            <div className="p-5">
              <h3 className="font-semibold text-gray-800 text-sm">
                {getMonthName(liveMonth.monthKey)} so far — first {liveMonth.equalPeriod.days} days
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Measured against the same {liveMonth.equalPeriod.days} days of the previous month. A run rate
                assumes the rest of the month looks like the part already seen; this makes no such assumption.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                {[
                  { label: 'Revenue', now: liveMonth.equalPeriod.revenue, then: liveMonth.equalPeriod.prevRevenue, fmt: (v) => tl(v) },
                  { label: 'Product views', now: liveMonth.equalPeriod.views, then: liveMonth.equalPeriod.prevViews, fmt: (v) => int(v) },
                  { label: 'Purchases', now: liveMonth.equalPeriod.purchases, then: liveMonth.equalPeriod.prevPurchases, fmt: (v) => int(v) },
                ].map((m) => {
                  const delta = m.then > 0 ? ((m.now - m.then) / m.then) * 100 : null;
                  const up = delta !== null && delta >= 0;
                  return (
                    <div key={m.label} className="rounded-lg border border-gray-200 p-3">
                      <div className="text-xs text-gray-500">{m.label}</div>
                      <div className="text-xl font-semibold text-gray-800 mt-0.5">{m.fmt(m.now)}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        prev {m.fmt(m.then)}
                        {delta !== null && (
                          <span className={`ml-1.5 font-medium ${up ? 'text-emerald-600' : 'text-red-500'}`}>
                            {up ? '+' : ''}{delta.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {showMethodology && (
          <Card className="rounded-2xl">
            <div className="p-5 text-sm text-gray-600 space-y-3">
              <h3 className="font-semibold text-gray-800">How this page is calculated</h3>
              <ol className="list-decimal ml-5 space-y-2">
                <li>
                  <strong>Source de-duplication.</strong> GA4 splits one SKU across several rows when brand casing
                  or category differs (<code>ASICS</code> vs <code>Asics</code>). Rows carrying revenue often carry
                  zero views. Everything is summed per item first, so revenue is no longer discarded.
                </li>
                <li>
                  <strong>Minimum views floor.</strong> Products below the floor go to <em>Insufficient Data</em>.
                  The floor is the strictest value from 50/30/20/10/5/2/1 that still leaves a workable population.
                  {selectedMonth && ` For ${getMonthName(selectedMonthKey)} it is ${int(selectedMonth.thresholds?.minViewsThreshold)} views.`}
                </li>
                <li>
                  <strong>No Sales tier.</strong> Products with traffic but zero purchases are reported separately.
                  With no sales, conversion rate and revenue per view are both zero, so such a product has no
                  position on the matrix.
                </li>
                <li>
                  <strong>Empirical-Bayes shrinkage.</strong> Each product is credited with{' '}
                  {selectedMonth ? Number(selectedMonth.thresholds?.ebPriorViews || 0).toFixed(0) : 'k'} pseudo-views
                  of portfolio-average behaviour before its own numbers count. A product with 60 views no longer
                  outranks one with 6.000.
                </li>
                <li>
                  <strong>Thresholds.</strong> The true median of the shrunk metrics across products that converted.
                  {selectedMonth && ` CR ${pct(selectedMonth.thresholds?.crThreshold ?? 0, 3)} · RPV ${tl(selectedMonth.thresholds?.rpvThreshold ?? 0, 2)}.`}
                </li>
                <li>
                  <strong>Revenue core.</strong> A below-median product that still sits in the top 80% of cumulative
                  revenue is treated as a Cash Cow — it earns real money, it just converts inefficiently.
                </li>
                <li>
                  <strong>Growth matrix.</strong> The classical BCG axes: relative revenue share against the largest
                  product (x) and month-over-month revenue growth against the portfolio's own growth (y).
                </li>
                <li>
                  <strong>Frozen months.</strong> A month's source data stops changing on the 1st of the
                  following month, so each closed month is computed once — from the 2nd — and never
                  recomputed. Its "computed" timestamp stays old on purpose; only the month in progress is
                  recalculated nightly.
                </li>
                <li>
                  <strong>Held verdicts.</strong> One month's quadrant is too volatile to act on: between
                  consecutive months only about a fifth to a third of products keep theirs, because roughly
                  half of the converting SKUs sell a single unit. <em>Held</em> marks a product that kept the
                  same quadrant in at least 2 of the last 3 closed months — that is what an exclusion list or
                  a catalogue set should be built from, not one month's membership.
                </li>
              </ol>
              <p className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                <strong>Ad spend caveat:</strong> revenue is all-channel GA4 revenue while cost is Google Shopping
                spend only. "Revenue per ad ₺" is therefore not an attributed ROAS — read it as a coverage ratio.
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* ── Matrix chart ── */}
      {!loading && !error && scatterData.products.length > 0 && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {matrixMode === 'growth' ? 'Growth–Share Matrix' : 'Performance Matrix — Product Positioning'}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {int(scatterData.products.length)} products plotted for {getMonthName(selectedMonthKey)} · bubble size = revenue
                  {scatterData.dropped > 0 && ` · ${int(scatterData.dropped)} not plottable on this view`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
                  {[['performance', 'Performance'], ['growth', 'Growth–Share']].map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setMatrixMode(k)}
                      className={`px-3 py-1.5 text-xs font-medium transition ${
                        matrixMode === k ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setLogScale(v => !v)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition ${
                    logScale ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                  title="Product metrics are heavily skewed; a linear axis pushes almost every point into one corner."
                >
                  Log scale
                </button>
                <button
                  onClick={() => setScatterChartExpanded(v => !v)}
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  title={scatterChartExpanded ? 'Collapse' : 'Expand'}
                >
                  <Maximize2 className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3 flex-wrap">
              {['Stars', 'Question Marks', 'Cash Cows', 'Dogs'].map(k => (
                <div key={k} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: BCG_COLORS[k] }} />
                  <span className="text-xs text-gray-500">{k}</span>
                </div>
              ))}
              <div className="flex items-center gap-1.5 ml-3 pl-3 border-l border-gray-200">
                <div className="w-3 h-0.5 bg-red-400" />
                <span className="text-xs text-gray-400">
                  {matrixMode === 'growth'
                    ? `Portfolio growth ${pct(scatterData.yThreshold, 1)} · median relative share`
                    : `Thresholds — CR ${pct(scatterData.yThreshold, 3)} · RPV ${tl(scatterData.xThreshold, 2)}`}
                </span>
              </div>
            </div>
          </div>

          <div className={`px-2 py-3 ${scatterChartExpanded ? 'h-[600px]' : 'h-[440px]'} transition-all duration-300`}>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 30, bottom: 24, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  type="number"
                  dataKey="x"
                  scale={logScale ? 'log' : 'linear'}
                  domain={axisDomains.x}
                  allowDataOverflow
                  stroke="#9ca3af"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => (matrixMode === 'growth'
                    ? `${(v * 100).toFixed(v < 0.01 ? 2 : 0)}%`
                    : `₺${Number(v).toFixed(Number(v) < 10 ? 1 : 0)}`)}
                  label={{
                    value: matrixMode === 'growth'
                      ? 'Relative revenue share (vs largest product) →'
                      : 'Revenue per view, shrunk →',
                    position: 'insideBottom', offset: -14, fill: '#6b7280', fontSize: 12,
                  }}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  scale={logScale && matrixMode !== 'growth' ? 'log' : 'linear'}
                  domain={axisDomains.y}
                  allowDataOverflow
                  stroke="#9ca3af"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${Number(v).toFixed(Number(v) < 1 ? 2 : 1)}%`}
                  label={{
                    value: matrixMode === 'growth'
                      ? '← Revenue growth MoM'
                      : '← Conversion rate, shrunk',
                    angle: -90, position: 'insideLeft', offset: 5, fill: '#6b7280', fontSize: 12,
                  }}
                />
                <ZAxis type="number" dataKey="revenue" range={[30, 380]} name="Revenue" />
                <ReferenceLine x={scatterData.xThreshold} stroke="#ef4444" strokeDasharray="6 4" strokeWidth={1.5} strokeOpacity={0.6} />
                <ReferenceLine y={scatterData.yThreshold} stroke="#ef4444" strokeDasharray="6 4" strokeWidth={1.5} strokeOpacity={0.6} />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3', stroke: '#d1d5db' }}
                  content={({ active, payload }) => {
                    if (!active || !payload || !payload.length) return null;
                    const d = payload[0]?.payload;
                    if (!d) return null;
                    const catColor = BCG_COLORS[d.category] || '#6b7280';
                    return (
                      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-3 max-w-[300px]">
                        <div className="flex items-start gap-2">
                          {d.imageLink && <img src={d.imageLink} alt="" className="w-10 h-10 rounded border object-cover flex-shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 leading-snug break-words">{d.name}</p>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: catColor }} />
                              <span className="text-[10px] font-medium" style={{ color: catColor }}>{d.category}</span>
                              {d.badge && (
                                <span className="text-[9px] px-1 py-0.5 bg-amber-50 text-amber-700 rounded border border-amber-200">{d.badge}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2.5 text-xs">
                          <div className="text-gray-500">CR</div><div className="text-right font-medium text-gray-800">{pct(d.cr)}</div>
                          <div className="text-gray-500">RPV</div><div className="text-right font-medium text-gray-800">{tl(d.rpv, 2)}</div>
                          {d.aov !== null && d.aov !== undefined && (<><div className="text-gray-500">AOV</div><div className="text-right font-medium text-gray-800">{tl(d.aov)}</div></>)}
                          <div className="text-gray-500">Revenue</div><div className="text-right font-medium text-gray-800">{tl(d.revenue)}</div>
                          <div className="text-gray-500">Views</div><div className="text-right font-medium text-gray-800">{int(d.views)}</div>
                          <div className="text-gray-500">Purchases</div><div className="text-right font-medium text-gray-800">{int(d.purchases)}</div>
                        </div>
                      </div>
                    );
                  }}
                />
                <Scatter data={scatterData.products} isAnimationActive={false}>
                  {scatterData.products.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={BCG_COLORS[entry.category] || '#9ca3af'}
                      fillOpacity={0.6}
                      stroke={BCG_COLORS[entry.category] || '#9ca3af'}
                      strokeWidth={1}
                      strokeOpacity={0.9}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          <div className="px-5 pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ROWS.filter(r => ['Question Marks', 'Stars', 'Dogs', 'Cash Cows'].includes(r.key)).map(r => {
                const t = TONE[r.tone];
                return (
                  <div key={r.key} className={`rounded-lg ${t.bg} border ${t.border} p-2.5`}>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">{r.icon}</span>
                      <span className={`text-xs font-medium ${t.text}`}>{r.key}</span>
                    </div>
                    <p className={`text-[10px] ${t.text} opacity-80 mt-0.5`} title={r.detail}>{r.blurb}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {/* ── Tier summary ── */}
      {!loading && !error && selectedMonth?.tiers && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'On the matrix', value: selectedMonth.tiers.converting, sub: 'converted at least once', tone: 'text-emerald-600' },
            { label: 'No sales', value: selectedMonth.tiers.noSales, sub: `${int(selectedMonth.tiers.noSalesViews)} views, zero revenue`, tone: 'text-red-600' },
            { label: 'Insufficient data', value: selectedMonth.tiers.insufficientData, sub: `below ${int(selectedMonth.thresholds?.minViewsThreshold)} views · ${tl(selectedMonth.tiers.insufficientDataRevenue)}`, tone: 'text-slate-500' },
            { label: 'Shopping spend', value: null, money: selectedMonth.spend?.totalAdCost, sub: `${int(selectedMonth.spend?.totalAdClicks)} clicks`, tone: 'text-blue-600' },
          ].map((c) => (
            <Card key={c.label} className="rounded-xl">
              <div className="p-4">
                <div className="text-xs text-gray-500">{c.label}</div>
                <div className={`text-2xl font-semibold mt-1 ${c.tone}`}>
                  {c.value !== null && c.value !== undefined ? int(c.value) : tl(c.money)}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">{c.sub}</div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── Matrix table ── */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-xs sm:text-sm text-gray-600">Product lists show for</div>
            <select
              className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={selectedMonthKey || ''}
              onChange={(e) => setSelectedMonthKey(e.target.value)}
            >
              {availableMonths.map((m) => (
                <option key={m} value={m}>{getMonthName(m)}</option>
              ))}
            </select>
            {selectedMonth?.coverage?.isPartial && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                Partial month · {selectedMonth.coverage.daysWithData}/{selectedMonth.coverage.daysInMonth} days
              </span>
            )}
            {selectedMonth?.finalizedAt && (
              <span
                className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 border border-gray-200"
                title="This month's source data can no longer change, so it is computed once and never recomputed."
              >
                Frozen
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              {[['compact', 'Shares'], ['detailed', 'All metrics']].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setDensity(k)}
                  className={`px-3 py-1.5 text-xs font-medium transition ${
                    density === k ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                  title={k === 'compact'
                    ? 'Share of views against share of revenue — where attention goes versus where the money comes from'
                    : 'Add conversion, RPV, AOV, add-to-cart and Shopping spend to every cell'}
                >
                  {label}
                </button>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer" title="Only products that kept the same quadrant in at least 2 of the last 3 closed months. One month's membership is too volatile to push to an ad platform.">
              <input type="checkbox" checked={stableOnly} onChange={e => setStableOnly(e.target.checked)} className="rounded" />
              <span className="text-xs font-medium text-gray-600">Export held verdicts only</span>
            </label>
          </div>
        </div>

        <div className="relative overflow-x-auto">
          <table className="w-full" style={{ minWidth: `${240 + (availableMonths.length || 4) * (density === 'compact' ? 168 : 190)}px` }}>
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-3 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 w-[240px]">Category</th>
                {(availableMonths.length ? availableMonths : ['—', '—', '—', '—']).map((monthKey, i) => {
                  const m = months.find(x => x.monthKey === monthKey);
                  const isTargetCol = monthKey === productData?.targetMonthKey && !!productData?.targetByRefCategory;
                  return (
                    <th key={`${monthKey}-${i}`} className="px-3 py-3 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider w-[190px]">
                      {getMonthName(monthKey)}
                      {m?.coverage?.isPartial && (
                        <div className="text-[10px] font-normal normal-case text-blue-500 mt-0.5">
                          partial ({m.coverage.daysWithData}/{m.coverage.daysInMonth}d)
                        </div>
                      )}
                      {m?.finalizedAt && (
                        <div
                          className="text-[10px] font-normal normal-case text-gray-400 mt-0.5"
                          title="Computed once and frozen — its inputs can no longer change."
                        >
                          frozen
                        </div>
                      )}
                      {isTargetCol && (
                        <div
                          className="text-[10px] font-normal normal-case text-gray-400 mt-0.5"
                          title={`Quadrant rows track the ${getMonthName(productData.referenceMonthKey)} cohorts into the month in progress, so they are comparable month to month. The No Sales and Insufficient Data rows are this month's own figures.`}
                        >
                          {getMonthName(productData.referenceMonthKey)} cohorts, tracked
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
              <tr className="bg-gray-50 border-t border-gray-200">
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 sticky left-0 bg-gray-50 z-10">
                  Quadrant thresholds
                  <div className="text-[10px] font-normal text-gray-400 normal-case">
                    median of shrunk metrics, converting products
                  </div>
                </th>
                {(availableMonths.length ? availableMonths : ['—', '—', '—', '—']).map((monthKey, i) => {
                  const m = months.find(x => x.monthKey === monthKey);
                  const th = m?.thresholds;
                  return (
                    <th key={`${monthKey}-th-${i}`} className="px-3 py-2 text-right text-xs font-medium text-gray-600">
                      {th ? (
                        <>
                          CR ≥ {pct(th.crThreshold ?? th.crMedian, 3)}<br />
                          RPV ≥ {tl(th.rpvThreshold ?? th.rpvMedian, 2)}
                          <div className="text-[10px] font-normal text-gray-400">min {int(th.minViewsThreshold)} views</div>
                        </>
                      ) : '—'}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={1 + (availableMonths.length || 4)} className="px-4 py-8 text-center">
                    <div className="flex items-center justify-center space-x-3">
                      <img src="/logoicon.png" alt="Loading..." className={loadingStyles.animatedLogo} />
                      <span className="text-gray-500">Loading portfolio data...</span>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={1 + (availableMonths.length || 4)} className="px-4 py-8 text-center">
                    <div className="text-red-600">Error loading data: {error}</div>
                  </td>
                </tr>
              ) : !months.length ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No portfolio snapshots for this brand yet.
                  </td>
                </tr>
              ) : (
                <>
                  {ROWS.map(row => {
                    const t = TONE[row.tone];
                    const isTierRow = row.key === 'Insufficient Data';
                    return (
                      <React.Fragment key={row.key}>
                        <tr className={`${t.hover} transition-colors duration-150`}>
                          <td className={`px-4 py-4 sticky left-0 bg-white z-10 ${t.hover}`}>
                            <div className="flex items-center space-x-3">
                              <div className={`flex-shrink-0 w-10 h-10 rounded-full ${t.chip} flex items-center justify-center`}>
                                <span className="text-xl">{row.icon}</span>
                              </div>
                              <div className="flex flex-col min-w-0">
                                <span className="font-medium text-gray-900 text-base">{row.key}</span>
                                <span className="text-gray-500 text-sm">
                                  Products: {int(rowValue(selectedMonth || {}, row.key)?.productCount)}
                                </span>
                              </div>
                              <div className="ml-auto flex items-center gap-2">
                                <button
                                  onClick={() => toggleCategory(row.key)}
                                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${t.border} ${t.text} ${t.hover}`}
                                >
                                  {expandedCategories[row.key] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  View
                                </button>
                                <button
                                  onClick={() => handleExportCategoryIds(row.key)}
                                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${t.border} ${t.text} ${t.hover}`}
                                  title="Export product IDs (CSV)"
                                >
                                  <Download className="w-4 h-4" />
                                  IDs
                                </button>
                                {row.key === 'Dogs' && (
                                  <button
                                    onClick={() => handleExportCategoryIds('No Sales')}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50"
                                    title="Export only the products that sold nothing — the half of this row that needs a different action"
                                  >
                                    <Download className="w-4 h-4" />
                                    Zero-sale IDs
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-[11px] text-gray-400 mt-2 leading-snug" title={row.detail}>
                              {row.blurb}
                            </p>
                          </td>

                          {months.map(month => {
                            const isTarget = month.monthKey === productData?.targetMonthKey;
                            const raw = rowValue(month, row.key);
                            const d = (isTarget && productData?.targetByRefCategory && !isTierRow && row.key !== 'No Sales')
                              ? (productData.targetByRefCategory[row.key] || raw)
                              : raw;
                            const f = month.coverage?.runRateFactor || 1;
                            const scaled = (v) => scaleFor(month, v);
                            return (
                              <td key={month.monthKey} className="px-3 py-3 align-top">
                                <div className="text-right">
                                  <div className="font-semibold text-gray-900 text-[15px] tabular-nums">
                                    {tl(scaled(d.totalRevenue))}
                                    {useRunRate && f > 1 && <span className="text-[10px] text-blue-500 ml-1">run-rate</span>}
                                  </div>

                                  {/* The comparison the page exists for: the share of
                                      attention a tier takes against the share of money
                                      it returns. Both bars use one scale so their
                                      lengths are directly comparable. */}
                                  <ShareBars
                                    revenuePct={d.revenueShare}
                                    viewsPct={d.viewsPercentage}
                                    tone={t}
                                  />

                                  {density === 'detailed' && (
                                    <div className="mt-2 pt-1.5 border-t border-dashed border-gray-200 space-y-0.5 text-[12px] text-gray-500 tabular-nums">
                                      <div>{int(scaled(d.totalViews))} views · {int(d.productCount)} products</div>
                                      {row.key !== 'Insufficient Data' && (
                                        <>
                                          <div>CR {pct(d.cr)} · RPV {tl(d.rpv, 2)}</div>
                                          {d.aov > 0 && <div>AOV {tl(d.aov)}{d.atcRate > 0 ? ` · ATC ${pct(d.atcRate, 1)}` : ''}</div>}
                                          {d.totalAdCost > 0 && (
                                            <div
                                              className="text-gray-400"
                                              title="Product-level Google Shopping spend only — measured at about a fifth of total Google Ads spend, so this is a coverage ratio, not a return."
                                            >
                                              Shopping {tl(scaled(d.totalAdCost))}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  )}

                                  {density === 'compact' && (
                                    <div className="text-[11px] text-gray-400 mt-1 tabular-nums">
                                      {int(d.productCount)} products
                                    </div>
                                  )}

                                  {row.key === 'Dogs' && month.categories?.['No Sales']?.productCount > 0 && (
                                    <div className="text-[11px] text-red-500 mt-1 tabular-nums">
                                      {int(month.categories['No Sales'].productCount)} sold nothing
                                    </div>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>

                        {expandedCategories[row.key] && (
                          <tr>
                            <td colSpan={1 + months.length} className={`px-4 pb-4 ${t.bg}`}>
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {rowProducts(row.key).map((p) => (
                                  <div key={p.itemId || p.productName} className={`rounded-lg border ${t.border} bg-white p-3 hover:shadow-sm transition`}>
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="font-medium text-gray-800 text-sm leading-snug break-words whitespace-normal" title={p.productName}>
                                        {p.productName}
                                      </div>
                                      <span className={`text-[10px] px-2 py-0.5 rounded ${t.chip} ${t.text} border ${t.border} flex-shrink-0`}>
                                        CR {pct(p.cr)}
                                      </span>
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {p.stableCategory && p.stableCategory === p.category && (
                                        <Badge tone="emerald" title={`Same quadrant in ${p.stableHits} of the last 3 closed months`}>
                                          Held {p.stableHits}/3
                                        </Badge>
                                      )}
                                      {p.workhorse && <Badge tone="amber" title={p.workhorseReason}>Revenue core</Badge>}
                                      {p.dataTier === 'no_sales' && <Badge tone="red" title="Traffic but zero purchases this month">Sold nothing</Badge>}
                                      {p.isOutOfStock && <Badge tone="red">Out of stock</Badge>}
                                      {p.isNew && <Badge tone="violet">New this month</Badge>}
                                      {p.growthCategory && p.growthCategory !== 'New' && <Badge tone="slate">Growth: {p.growthCategory}</Badge>}
                                    </div>
                                    <div className="mt-1 text-[10px] text-gray-500 truncate">ID: {p.itemId || '-'}</div>
                                    {p.imageLink && (
                                      <div className="mt-2">
                                        <img src={p.imageLink} alt={p.productName} className="h-16 w-16 rounded border object-cover" />
                                      </div>
                                    )}
                                    <div className="mt-2 flex items-center justify-between text-sm">
                                      <span className="text-gray-500">Revenue</span>
                                      <span className="font-semibold">{tl(p.totalRevenue)}</span>
                                    </div>
                                    <div className="mt-1 grid grid-cols-2 gap-x-2 text-xs text-gray-600">
                                      <span>Views {int(p.totalViews)}</span>
                                      <span className="text-right">Purchases {int(p.totalPurchases)}</span>
                                      {p.aov != null && <span>AOV {tl(p.aov)}</span>}
                                      {p.atcRate > 0 && <span className="text-right">ATC {pct(p.atcRate, 1)}</span>}
                                      {p.adCost > 0 && <span>Ad {tl(p.adCost)}</span>}
                                      {p.revenueGrowth != null && (
                                        <span className={`text-right ${p.revenueGrowth >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                          {p.revenueGrowth >= 0 ? '+' : ''}{Number(p.revenueGrowth).toFixed(0)}% MoM
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {!rowProducts(row.key).length && (
                                  <div className="text-sm text-gray-400 py-2">No products in this tier for {getMonthName(selectedMonthKey)}.</div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Total */}
                  <tr className="bg-gray-800 text-white font-medium border-t-2 border-gray-300">
                    <td className="px-4 py-4 sticky left-0 bg-gray-800 z-10">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                          <span className="text-white text-lg">∑</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="font-semibold text-white text-base">TOTAL</span>
                          <span className="text-gray-300 text-sm">Every tier, whole month</span>
                        </div>
                      </div>
                    </td>
                    {months.map(month => (
                      <td key={month.monthKey} className="px-4 py-4 text-right text-white">
                        <div className="text-base">{tl(scaleFor(month, month.totalRevenueAll))}</div>
                        <div className="text-sm mt-1 pt-1 border-t border-dashed border-gray-600">
                          On matrix: {tl(scaleFor(month, month.totalRevenue))}
                        </div>
                        <div className="text-sm text-gray-300">Products: {int(month.tiers?.totalProducts)}</div>
                      </td>
                    ))}
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Growth matrix summary ── */}
      {!loading && !error && selectedMonth?.growth && (
        <Card className="rounded-2xl">
          <div className="p-5">
            <h2 className="text-lg font-semibold text-gray-800">Growth–Share Matrix — {getMonthName(selectedMonthKey)}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Classical BCG axes. Growth is measured against the portfolio's own growth of{' '}
              {selectedMonth.growth.portfolioRevenueGrowth === null ? 'n/a' : pct(selectedMonth.growth.portfolioRevenueGrowth, 1)};
              share is relative to the largest product.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
              {['Stars', 'Question Marks', 'Cash Cows', 'Dogs', 'New'].map(k => {
                const agg = selectedMonth.growth.aggregates?.[k];
                const count = selectedMonth.growth.counts?.[k] || 0;
                const color = BCG_COLORS[k] || '#8b5cf6';
                return (
                  <div key={k} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-sm font-medium text-gray-700">{k}</span>
                      <span className="ml-auto text-sm font-semibold text-gray-800">{int(count)}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1.5 space-y-0.5">
                      <div>Revenue {tl(agg?.totalRevenue)}</div>
                      <div>Views {int(agg?.totalViews)}</div>
                      {agg?.totalAdCost > 0 && <div>Ad spend {tl(agg.totalAdCost)}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * The two shares a tier is judged on, drawn on one scale: how much of the
 * catalogue's product views it absorbed, and how much of the revenue it
 * returned. The ratio between them is the whole point — a tier taking 40% of
 * the attention for 8% of the money is the finding, and no column of raw
 * numbers makes that visible at a glance.
 */
function ShareBars({ revenuePct, viewsPct, tone }) {
  const r = Number(revenuePct);
  const v = Number(viewsPct);
  const hasR = Number.isFinite(r);
  const hasV = Number.isFinite(v);
  if (!hasR && !hasV) return null;
  const w = (x) => `${Math.max(1.5, Math.min(100, (Math.max(0, x) / SHARE_SCALE_MAX) * 100))}%`;
  const index = hasR && hasV && v > 0.01 ? r / v : null;
  const indexTone = index === null ? 'text-gray-400'
    : index >= 1.15 ? 'text-emerald-600'
    : index <= 0.85 ? 'text-red-500'
    : 'text-gray-500';

  const Track = ({ label, value, barClass }) => (
    <div className="flex items-center gap-1.5">
      <span className="text-[9px] uppercase tracking-wide text-gray-400 w-8 text-left flex-shrink-0">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden min-w-[24px]">
        <div className={`h-full rounded-full ${barClass}`} style={{ width: w(value) }} />
      </div>
      <span className="text-[10px] tabular-nums text-gray-500 w-9 text-right flex-shrink-0">
        {Number.isFinite(value) ? `${value.toFixed(1)}%` : '—'}
      </span>
    </div>
  );

  return (
    <div className="mt-2 space-y-1">
      <Track label="rev" value={r} barClass={tone.bar} />
      <Track label="view" value={v} barClass="bg-gray-300" />
      <div className={`text-[11px] tabular-nums font-medium ${indexTone}`} title="Revenue share divided by view share. Above 1 means the tier returns more money than the attention it takes.">
        {index === null ? '—' : `${index.toFixed(2)}×`}
        <span className="text-gray-300 font-normal ml-1">rev / view</span>
      </div>
    </div>
  );
}

function Badge({ tone, title, children }) {
  const map = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    slate: 'bg-slate-50 text-slate-600 border-slate-200',
  };
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded border ${map[tone] || map.slate}`} title={title || undefined}>
      {children}
    </span>
  );
}
